/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import { fetchAttendancesTimelines } from "../../../../services";

// Shared hook to fetch and process today's outside employees
// Uses the exact same calculation logic as Timeline/index.js
function useTodayOutsideEmployees() {
  const from = moment().startOf("day").format("YYYY-MM-DD HH:mm:ss");
  const to = moment().endOf("day").format("YYYY-MM-DD HH:mm:ss");

  // Keep it dynamic/today but allow standard query parameters
  const { data, isLoading } = useQuery(
    ["attendances", "today-outside-merged", from, to],
    () => fetchAttendancesTimelines(from, to),
    {
      cacheTime: 60 * 1000,
      staleTime: 30 * 1000,
      refetchInterval: 30000,
    }
  );

  const rawEvents = data?.data?.data || [];

  const processedEmployees = React.useMemo(() => {
    if (!rawEvents || rawEvents.length === 0) return [];

    // Group transactions by employee ID and date string (YYYY-MM-DD) — same as Timeline
    const employeeDayTxs = {};
    rawEvents.forEach((tx) => {
      const empId = tx.employee_id || tx.employee?.id || "unknown";
      const dateKey = tx.transacted_at
        ? moment.utc(tx.transacted_at).format("YYYY-MM-DD")
        : "unknown-date";
      const key = `${empId}_${dateKey}`;
      if (!employeeDayTxs[key]) employeeDayTxs[key] = { employee: tx.employee, txs: [] };
      employeeDayTxs[key].txs.push(tx);
    });

    const employeeTotals = {};

    Object.keys(employeeDayTxs).forEach((key) => {
      const { employee, txs } = employeeDayTxs[key];
      const empId = employee?.id || key;

      // Sort ascending by time within the same day — same as Timeline
      const list = [...txs].sort(
        (a, b) => moment(a.transacted_at).valueOf() - moment(b.transacted_at).valueOf()
      );

      let dayTotalOutsideSeconds = 0;
      let lastOutTx = null;
      let isCurrentlyOutside = false;
      let hasOver2hSession = false;

      list.forEach((tx) => {
        const action = (tx.action || tx.device?.type || "").toLowerCase();
        if (action === "out") {
          lastOutTx = tx;
          isCurrentlyOutside = true;
        } else if (action === "in" && lastOutTx) {
          const start = moment.utc(lastOutTx.transacted_at);
          const end = moment.utc(tx.transacted_at);

          if (start.isSame(end, "day")) {
            const durationSeconds = end.diff(start, "seconds");

            // Do not count exits after midnight as outside time.
            if (durationSeconds > 4 * 60 * 60) {
              lastOutTx = null;
              isCurrentlyOutside = false;
              return;
            }

            if (durationSeconds >= 0) {
              dayTotalOutsideSeconds += durationSeconds;
              if (durationSeconds > 7200) {
                hasOver2hSession = true;
              }
            }
          }

          lastOutTx = null;
          isCurrentlyOutside = false;
        }
      });

      if (!employeeTotals[empId]) {
        employeeTotals[empId] = {
          employee,
          totalOutsideSeconds: 0,
          isCurrentlyOutside: false,
          hasOver2hSession: false,
        };
      }

      employeeTotals[empId].totalOutsideSeconds += dayTotalOutsideSeconds;
      employeeTotals[empId].hasOver2hSession = employeeTotals[empId].hasOver2hSession || hasOver2hSession;

      if (isCurrentlyOutside) {
        employeeTotals[empId].isCurrentlyOutside = true;
      }
    });

    const list = Object.values(employeeTotals).filter(
      (emp) => emp.totalOutsideSeconds > 1800 // > 30 minutes
    );

    return list.sort((a, b) => b.totalOutsideSeconds - a.totalOutsideSeconds);
  }, [rawEvents]);

  return { processedEmployees, isLoading };
}

function formatDuration(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function EmployeeRow({ item }) {
  const isOver2h = item.hasOver2hSession || item.totalOutsideSeconds > 7200;
  const employeeName = item.employee?.name || "Unknown Employee";

  return (
    <div className="d-flex align-items-center mb-6">
      <div className={`symbol symbol-40 mr-5 symbol-light-${isOver2h ? 'danger' : 'warning'}`}>
        <span className={`symbol-label font-size-h5 font-weight-bold text-${isOver2h ? 'danger' : 'warning'}`}>
          {employeeName ? employeeName.charAt(0).toUpperCase() : '?'}
        </span>
      </div>
      <div className="d-flex flex-column flex-grow-1 font-weight-bold">
        <span className="text-dark-75 mb-1 font-size-lg">{employeeName}</span>
      </div>
      <span className={`label label-lg ${isOver2h ? 'label-light-danger text-danger' : 'label-light-warning text-warning'} label-inline font-weight-bold py-4`}>
        {formatDuration(item.totalOutsideSeconds)}
      </span>
    </div>
  );
}

/**
 * Single Box Widget: Time spent out of office
 * With tabs for "> 30m & < 2h" and "> 2h"
 */
export function OutsideEmployeesWidget({ className }) {
  const [activeTab, setActiveTab] = useState('30m'); // '30m' or '2h'
  const { processedEmployees, isLoading } = useTodayOutsideEmployees();

  // Filter employees based on active tab
  const employees = React.useMemo(() => {
    if (activeTab === '2h') {
      return processedEmployees.filter(emp => emp.hasOver2hSession || emp.totalOutsideSeconds > 7200);
    }
    // activeTab === '30m' (represents > 30m & < 2h)
    return processedEmployees.filter(emp => !emp.hasOver2hSession && emp.totalOutsideSeconds <= 7200);
  }, [processedEmployees, activeTab]);

  return (
    <div className={`card card-custom ${className}`}>
      {/* Header */}
      <div className="card-header align-items-center border-0 mt-4">
        <h3 className="card-title align-items-start flex-column">
          <span className="font-weight-bolder text-dark">
            Time spent out of office
          </span>
          <span className="text-muted mt-3 font-weight-bold font-size-sm">
            {employees.length} employee{employees.length !== 1 ? 's' : ''} shown
          </span>
        </h3>
        <div className="card-toolbar">
          <ul className="nav nav-pills nav-pills-sm">
            <li className="nav-item">
              <button
                className={`nav-link py-2 px-4 font-weight-bold ${activeTab === '30m' ? 'text-warning font-weight-bolder' : 'text-muted'
                  }`}
                onClick={() => setActiveTab('30m')}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: 'none'
                }}
              >
                &gt; 30m
              </button>
            </li>

            <li className="nav-item">
              <button
                className={`nav-link py-2 px-4 font-weight-bold ${activeTab === '2h' ? 'text-danger font-weight-bolder' : 'text-muted'
                  }`}
                onClick={() => setActiveTab('2h')}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: 'none'
                }}
              >
                &gt; 2h
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Body */}
      <div className="card-body pt-4" style={{ height: "250px", overflowY: "auto" }}>
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <div className="spinner spinner-track spinner-primary spinner-lg"></div>
          </div>
        ) : employees.length === 0 ? (
          <div className="d-flex flex-column justify-content-center align-items-center h-100 text-muted">
            <i className="fa fa-user-check mb-2 font-size-h3 text-success"></i>
            <span>No employees in this list today</span>
          </div>
        ) : (
          employees.map((item) => <EmployeeRow key={item.employee.id} item={item} />)
        )}
      </div>
    </div>
  );
}
